import { type FC, useRef } from 'react';
import type { LinksFunction } from '@remix-run/node';
import type { DbooRunScanFormProps } from '@/components/dboo/dboo-run-scan-form/dboo-run-scan-form.types';

import { useNavigate } from '@remix-run/react';
import { useDbooContext } from '@/contexts/DbooContext';

import { SharedValidationForm } from '@/components/shared/shared-validation-form/shared-validation-form';
import {
  DbooButton,
  links as dbooButtonLinks,
} from '@/components/dboo/dboo-button/dboo-button';
import {
  DbooInput,
  links as dbooInputLinks,
} from '@/components/dboo/dboo-input/dboo-input';
import {
  SharedDropdown,
  links as sharedDropdownLinks,
} from '@/components/shared/shared-dropdown/shared-dropdown';
import { handleValidateEmail } from '@/utils/emailValidation';
import {
  DbooLegalText,
  links as dbooLegalTextLinks,
} from '../dboo-legal-text/dboo-legal-text';

import { DBOO_SCANNING } from '@/constants/dboo/paths';
import { LANDING_TYPE } from '@/types/landingTypes';
import { US_STATES } from '@/constants/dboo/states';

import styles from './dboo-run-scan-form.css?url';

export const links: LinksFunction = () => {
  return [
    { rel: 'stylesheet', href: styles },
    ...dbooInputLinks(),
    ...sharedDropdownLinks(),
    ...dbooButtonLinks(),
    ...dbooLegalTextLinks(),
  ];
};

export const DbooRunScanForm: FC<DbooRunScanFormProps> = ({
  actionLabel = 'Protect my privacy',
}) => {
  const navigate = useNavigate();
  const { updateState } = useDbooContext();
  const emailInput = useRef<HTMLInputElement>(null);

  const handleRunScan = (formData: Record<string, string>) => {
    updateState({
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      city: formData.city,
      state: formData.state,
    });
    navigate(DBOO_SCANNING);
  };

  return (
    <SharedValidationForm
      onSubmit={handleRunScan}
      className="dboo-run-scan-form"
      customValidators={{
        email: handleValidateEmail(emailInput),
      }}
    >
      <DbooInput
        type="email"
        placeholder="Email"
        required
        minLength={2}
        name="email"
        autoComplete="email"
        ref={emailInput}
      />
      <fieldset className="dboo-run-scan-form__fieldset">
        <DbooInput
          type="text"
          placeholder="First name"
          required
          minLength={2}
          maxLength={25}
          name="firstName"
          autoComplete="given-name"
          pattern="^[a-zA-Z\-]+$"
        />
        <DbooInput
          type="text"
          placeholder="Last name"
          required
          minLength={2}
          maxLength={25}
          name="lastName"
          autoComplete="family-name"
          pattern="^[a-zA-Z\-]+$"
        />
      </fieldset>
      <fieldset className="dboo-run-scan-form__fieldset">
        <DbooInput
          type="text"
          placeholder="City"
          required
          minLength={3}
          maxLength={15}
          name="city"
          pattern="^[a-zA-Z' \-]+$"
          autoComplete="address-level2"
        />
        <SharedDropdown
          placeholder="State"
          landingType={LANDING_TYPE.DBOO}
          options={US_STATES}
          required
          name="state"
          autoComplete="address-level1"
        />
      </fieldset>

      <DbooLegalText actionLabel={actionLabel} />

      <DbooButton
        type="submit"
        variant="secondary"
        className="dboo-run-scan-form__cta"
      >
        {actionLabel}
      </DbooButton>
    </SharedValidationForm>
  );
};
